import { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders, API_BASE_URL } from './auth';
import { track } from './activity/track';
import './translations';

function cardOf(apiUrl) {
  try { return new URL(apiUrl, window.location.origin).searchParams.get('summary_type') || ''; } catch (e) { return ''; }
}

function getSummaryApiUrl(apiUrl, activeTab) {
  return apiUrl + (activeTab === 'kurz' ? '&summary_length=quick_info' : '&summary_length=morning_briefing');
}

function stripHtml(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Custom minimal audio player — also used by the own audio cards (src/audio/). */
export function AudioPlayer({ src, card, length }) {
  const audioRef = useRef(null);
  // Seconds actually listened (seeking does not count), reported once when
  // the player goes away or the briefing ends — the owner's Activity view.
  const listened = useRef({ seconds: 0, last: null, ended: false });
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => {});
    }
  }, [isPlaying]);

  const handleProgressClick = useCallback((e) => {
    const audio = audioRef.current;
    const bar = progressRef.current;
    if (!audio || !bar || !audio.duration) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = pct * audio.duration;
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const l = listened.current;
    const report = () => {
      if (l.seconds >= 1) {
        track('audio_play', {
          area: 'cards', title: card || '', durationMs: l.seconds * 1000,
          detail: { seconds: Math.round(l.seconds), duration: Math.round(audio.duration || 0), length, completed: l.ended },
        });
      }
      l.seconds = 0; l.ended = false;
    };
    const onPlay = () => { setIsPlaying(true); l.last = audio.currentTime; };
    const onPause = () => { setIsPlaying(false); l.last = null; };
    const onEnded = () => { setIsPlaying(false); setProgress(0); l.last = null; l.ended = true; report(); };
    const onTimeUpdate = () => {
      if (l.last != null) {
        const delta = audio.currentTime - l.last;
        if (delta > 0 && delta < 2) l.seconds += delta;
        l.last = audio.currentTime;
      }
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
        setCurrentTime(audio.currentTime);
      }
    };
    const onLoaded = () => setDuration(audio.duration);

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('durationchange', onLoaded);

    return () => {
      report();
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('durationchange', onLoaded);
    };
  }, [src, card, length]);

  return (
    <div className="el-audio-player">
      <audio ref={audioRef} src={src} preload="metadata" />
      <IconButton
        onClick={togglePlay}
        size="small"
        sx={{
          color: 'primary.main',
          width: 32,
          height: 32,
          bgcolor: 'rgba(25, 208, 239, 0.1)',
          '&:hover': { bgcolor: 'rgba(25, 208, 239, 0.18)' },
        }}
      >
        {isPlaying ? <PauseIcon sx={{ fontSize: 16 }} /> : <PlayArrowIcon sx={{ fontSize: 16 }} />}
      </IconButton>
      <span className="el-audio-time">{formatTime(currentTime)}</span>
      <div
        className="el-audio-progress"
        ref={progressRef}
        onClick={handleProgressClick}
      >
        <div className="el-audio-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <span className="el-audio-time">{formatTime(duration)}</span>
    </div>
  );
}

export const SUMMARY_LOADING_STEPS = ['loadingReadingData', 'loadingAnalyzing', 'loadingMerging', 'loadingWriting'];
export const AUDIO_LOADING_STEPS = ['loadingVoicePrep', 'loadingAudioRender'];

/** Animated loading state: pulsing equalizer bars + a cycling status message. */
export function LoadingIndicator({ steps }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);

  useEffect(() => {
    setStep(0);
    if (steps.length <= 1) return undefined;
    // Advance through each step once, then hold on the last — never loop/repeat.
    let current = 0;
    const id = setInterval(() => {
      current += 1;
      setStep(current);
      if (current >= steps.length - 1) clearInterval(id);
    }, 1800);
    return () => clearInterval(id);
  }, [steps]);

  return (
    <Box className="el-audio-player" sx={{ position: 'absolute', inset: 0, justifyContent: 'flex-start', gap: 1.25, pl: 1.5, overflow: 'hidden' }}>
      <Box sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: '3px', height: 16, flexShrink: 0 }}>
        {[0, 1, 2, 3].map(bar => (
          <Box
            key={bar}
            sx={{
              width: 3,
              height: 16,
              borderRadius: '2px',
              bgcolor: 'primary.main',
              transformOrigin: 'bottom',
              animation: 'elEqualizer 0.9s ease-in-out infinite',
              animationDelay: `${bar * 0.12}s`,
            }}
          />
        ))}
      </Box>
      <Typography key={step} variant="body2" noWrap sx={{ color: 'text.secondary', fontSize: '12px', position: 'relative', top: '2px', animation: 'elFadeIn 0.4s ease' }}>
        {t(steps[step])}
      </Typography>
    </Box>
  );
}

/**
 * Audio summary content (short/long tabs, player, collapsible text).
 * Extracted from the former Expandable accordion so it can be embedded
 * directly inside an overlay (Dialog / bottom-sheet). Fetches lazily on
 * mount and whenever the active tab changes.
 */
function AudioSummaryPanel({ summaryApiUrl, language }) {
  const [tabIndex, setTabIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [data, setData] = useState({
    kurz: { summary: null, summaryLoading: false, summaryError: null, speechFile: null, speechLoading: false, speechError: null },
    lang: { summary: null, summaryLoading: false, summaryError: null, speechFile: null, speechLoading: false, speechError: null },
  });
  const { t } = useTranslation();
  const activeTab = tabIndex === 0 ? 'kurz' : 'lang';

  function refreshContent() {
    setData(prev => ({
      ...prev,
      [activeTab]: { summary: null, summaryLoading: false, summaryError: null, speechFile: null, speechLoading: false, speechError: null },
    }));
  }

  useEffect(() => {
    const fetchSpeech = async (text) => {
      if (!text) return;
      setData(prev => ({ ...prev, [activeTab]: { ...prev[activeTab], speechLoading: true, speechError: null } }));
      try {
        const response = await fetch(`${API_BASE_URL}/api/fetch_speech_from_text?text=${encodeURIComponent(text)}&language=${language}`, { headers: getAuthHeaders() });
        const json = await response.json();
        const audioDataUrl = `data:audio/mp3;base64,${json.speech_file}`;
        setData(prev => ({ ...prev, [activeTab]: { ...prev[activeTab], speechFile: audioDataUrl, speechLoading: false } }));
      } catch (error) {
        setData(prev => ({ ...prev, [activeTab]: { ...prev[activeTab], speechLoading: false, speechError: error.message } }));
      }
    };

    if (!data[activeTab].summary && !data[activeTab].summaryLoading) {
      const fetchSummary = async () => {
        setData(prev => ({ ...prev, [activeTab]: { ...prev[activeTab], summaryLoading: true, summaryError: null } }));
        try {
          const res = await fetch(getSummaryApiUrl(summaryApiUrl, activeTab), { headers: getAuthHeaders() });
          const json = await res.json();
          setData(prev => ({ ...prev, [activeTab]: { ...prev[activeTab], summary: json.summary, summaryLoading: false } }));
          fetchSpeech(stripHtml(json.summary.replace(/<\/h2>/g, '</h2>.')));
        } catch (error) {
          setData(prev => ({ ...prev, [activeTab]: { ...prev[activeTab], summaryLoading: false, summaryError: error.message } }));
        }
      };
      fetchSummary();
    }
  }, [activeTab, data, summaryApiUrl, language]);

  const tabData = data[activeTab];

  return (
    <Box>
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        sx={{
          mb: 1.5,
          minHeight: 32,
          '& .MuiTab-root': {
            minHeight: 32,
            py: 0.5,
            px: 1.5,
            fontSize: '13px',
            fontWeight: 500,
          },
        }}
      >
        <Tab label={t('shortSummary')} />
        <Tab label={t('longSummary')} />
      </Tabs>

      {tabData.summaryError ? (
        <Typography variant="body2" color="error" sx={{ py: 2 }}>
          Fehler: {tabData.summaryError}
        </Typography>
      ) : (
        <Box>
          {/* Audio player row — always rendered to reserve height; spinner overlays when loading */}
          <Box sx={{ position: 'relative' }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              visibility: (tabData.summaryLoading || tabData.speechLoading) ? 'hidden' : 'visible',
            }}>
              <Box sx={{ flex: 1 }}>
                <AudioPlayer key={tabData.speechFile || 'placeholder'} src={tabData.speechFile || ''}
                  card={cardOf(summaryApiUrl)} length={activeTab === 'kurz' ? 'quick_info' : 'morning_briefing'} />
              </Box>
              <IconButton
                onClick={() => setIsVisible(!isVisible)}
                size="small"
                sx={{
                  color: isVisible ? 'primary.main' : 'text.secondary',
                  '&:hover': { color: 'primary.main', bgcolor: 'rgba(25, 208, 239, 0.06)' },
                }}
                title={t('showText') || 'Show text'}
              >
                <DescriptionIcon sx={{ fontSize: 18 }} />
              </IconButton>
              {tabData.summary && (
                <IconButton
                  onClick={refreshContent}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main', bgcolor: 'rgba(25, 208, 239, 0.06)' },
                  }}
                  title={t('refresh') || 'Refresh'}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>

            {/* Loading overlay — animated multi-step indicator */}
            {(tabData.summaryLoading || tabData.speechLoading) && (
              <LoadingIndicator steps={tabData.summaryLoading ? SUMMARY_LOADING_STEPS : AUDIO_LOADING_STEPS} />
            )}
          </Box>

          {/* Summary text (collapsible) — only once this tab's text has loaded,
              so switching tabs never flashes an empty container. */}
          <Collapse in={isVisible && !!tabData.summary}>
            <Box
              sx={{
                mt: 1.5,
                px: 2,
                py: 1.5,
                borderRadius: '10px',
                bgcolor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                '& h2': {
                  color: 'primary.main',
                  fontSize: '15px',
                  fontWeight: 600,
                  mt: 2,
                  mb: 0.75,
                  letterSpacing: '-0.01em',
                },
                '& p': { fontSize: '13px', lineHeight: 1.7, color: 'text.secondary', my: 0.75 },
                // Sit flush to the padded top/bottom edges: the first element
                // (paragraph or heading) gets no top margin, the last no bottom margin.
                '& p:first-of-type, & h2:first-of-type': { mt: 0 },
                '& p:last-of-type': { mb: 0 },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mt: 1.5,
                  '& th, & td': {
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    p: 1,
                    fontSize: '12px',
                  },
                  '& th': {
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    fontWeight: 600,
                    color: 'text.primary',
                  },
                  '& td': { color: 'text.secondary' },
                },
              }}
              dangerouslySetInnerHTML={{ __html: tabData.summary }}
            />
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

export default AudioSummaryPanel;
